/**
 * WepinBackendAPI V2 Wrapper
 * V1 API를 완전히 V2로 대체
 */

import { apiInterceptor } from '@/networks/api/interceptor'
import {
  userAPIServiceV2,
  walletAPIServiceV2,
  accountAPIServiceV2,
  currencyAPIService,
  accountTokenAPIServiceV2,
  nftAPIService,
  transactionAPIServiceV2,
  droplinkApiService,
} from '@/networks/api/v2'
import { useCurrencyAPIStore } from '@/stores/api/currency'

// Currency 변환 헬퍼 함수
function currencyToId(currency?: string): number {
  const currencyMap: { [key: string]: number } = {
    KRW: 1,
    USD: 3,
    EUR: 5,
    JPY: 6,
  }
  return currency ? currencyMap[currency.toUpperCase()] || 3 : 3 // 기본값: USD (3)
}

function idToCurrency(currencyId?: number): string {
  const idMap: { [key: number]: string } = {
    1: 'KRW',
    3: 'USD',
    5: 'EUR',
    6: 'JPY',
  }
  return currencyId ? idMap[currencyId] || 'USD' : 'USD' // 기본값: USD
}

class WepinBackendAPIV2 {
  user: any
  wallet: any
  account: any
  coin: any
  token: any
  transaction: any
  accountToken: any
  nft: any
  nftcontract: any
  info: any
  project: any
  dropLink: any
  notice: any
  accountBalance: any

  private static instance: WepinBackendAPIV2

  constructor() {
    // User API Wrapper
    this.user = {
      login: async (_params: { idToken: string }) => {
        const response = await userAPIServiceV2.login()
        // V2 응답 구조: { success, data: { accessToken, refreshToken, user, twoFactorEnabled, walletId, ... } }
        // 또는 직접 { loginStatus, userInfo, token, walletId } 형태
        const responseData = (response as any).data || response

        // V2 user -> V1 userInfo 변환
        const user = responseData.user || {}

        // locale 변환: 'ko' -> 1, 'en' -> 2, 'ja' -> 3
        let localeId = 2
        if (user.locale) {
          switch (user.locale) {
            case 'ko':
            case 'ko-KR':
              localeId = 1
              break
            case 'en':
            case 'en-US':
              localeId = 2
              break
            case 'ja':
            case 'ja-JP':
              localeId = 3
              break
          }
        }

        // currency 변환
        const currencyId = currencyToId(user.currency)

        // 2FA 상태: twoFactorEnabled -> use2FA
        // V1 use2FA: 0=UNSET, 1=SET, 2=AUTHENTICATED, 3=RECOVERY_CHECKED
        let use2FA = 0
        if (responseData.twoFactorEnabled) {
          use2FA = 2 // AUTHENTICATED
        }

        const userInfo = {
          id: user.id,
          email: user.email,
          name: user.displayName || user.username,
          profileImage: user.profileImageUrl,
          localeId: localeId,
          currencyId: currencyId,
          use2FA: use2FA,
          createdTime: user.createdAt,
          _v2Locale: user.locale,
          _v2Currency: user.currency,
        }

        return {
          token: {
            access: responseData.accessToken,
            refresh: responseData.refreshToken,
          },
          userInfo: userInfo,
          walletId: responseData.walletId,
          userAppStatus: responseData.userAppStatus,
        }
      },
      logout: async () => {
        await userAPIServiceV2.logout()
        return { success: true }
      },
      getInfo: async () => {
        const userV2 = await userAPIServiceV2.getUserDetail()

        // V2 locale string -> V1 localeId number 변환
        // V2: locale = 'ko', 'en', 'ja' 또는 'ko-KR', 'en-US', 'ja-JP'
        // V1: localeId = 1 (ko), 2 (en), 3 (ja)
        let localeId = 2 // 기본값: en

        if (userV2.locale) {
          switch (userV2.locale) {
            case 'ko':
            case 'ko-KR':
              localeId = 1
              break
            case 'en':
            case 'en-US':
              localeId = 2
              break
            case 'ja':
            case 'ja-JP':
              localeId = 3
              break
            default:
              localeId = 2 // en
          }
        }

        // V2: currency string -> V1 currencyId number 변환
        // V2: currency = 'USD', 'KRW', 'JPY', 'EUR'
        // V1: currencyId = 3, 1, 6, 5
        const currencyId = currencyToId(userV2.currency)

        // V2: is2FAEnabled -> V1: use2FA
        // V1 use2FA 상태: 0=UNSET, 1=SET, 2=AUTHENTICATED, 3=RECOVERY_CHECKED
        // V2 API 응답 또는 로그인 시 저장된 localStorage 값 참조
        let use2FA = 0
        const storedTwoFactorEnabled = localStorage.getItem('twoFactorEnabled')
        if (
          userV2.is2FAEnabled ||
          (storedTwoFactorEnabled && storedTwoFactorEnabled === 'true')
        ) {
          use2FA = 2 // AUTHENTICATED (2FA가 활성화되어 있으면 인증된 상태)
        }

        // V1 형식으로 변환
        return {
          id: userV2.id,
          email: userV2.email,
          name: userV2.displayName,
          profileImage: userV2.profileImageUrl,
          localeId: localeId,
          currencyId: currencyId,
          use2FA: use2FA,
          createdTime: userV2.createdAt,
          // V2 원본 필드
          _v2Locale: userV2.locale,
          _v2Currency: userV2.currency,
        } as any
      },
      getPubKey: async () => {
        return await userAPIServiceV2.getPubKey()
      },
      checkEmailExist: async (params: { email: string }) => {
        // V2 API는 전체 응답을 반환 (isEmailExist, isEmailverified, providerIds)
        return await userAPIServiceV2.checkEmailExist(params.email)
      },
      verify: async (params: any) => {
        return await userAPIServiceV2.verifyUser(params)
      },
      updateLocale: async (params: { localeId: number }) => {
        const localeMap: { [key: number]: string } = {
          1: 'ko-KR',
          2: 'en-US',
          3: 'ja-JP',
        }
        const locale = localeMap[params.localeId] || 'en-US'
        const result = await userAPIServiceV2.updateUser({ locale })

        // V1 형식으로 반환
        let localeId = 2
        if (result.locale) {
          switch (result.locale) {
            case 'ko-KR':
              localeId = 1
              break
            case 'en-US':
              localeId = 2
              break
            case 'ja-JP':
              localeId = 3
              break
          }
        }

        // V2 currency -> V1 currencyId 변환
        const currencyId = currencyToId(result.currency)

        return {
          id: result.id,
          email: result.email,
          name: result.displayName,
          profileImage: result.profileImageUrl,
          localeId: localeId,
          currencyId: currencyId,
          createdTime: result.createdAt,
        } as any
      },
      updateUser: async (params: any) => {
        const updates: any = {}
        if (params.name) updates.displayName = params.name
        if (params.phoneNumber) updates.phoneNumber = params.phoneNumber

        // V1 localeId -> V2 locale 변환
        if (params.localeId) {
          const localeMap: { [key: number]: string } = {
            1: 'ko-KR',
            2: 'en-US',
            3: 'ja-JP',
          }
          updates.locale = localeMap[params.localeId] || 'en-US'
        } else if (params.locale) {
          updates.locale = params.locale
        }

        // V1 currencyId -> V2 currency 변환
        if (params.currencyId) {
          updates.currency = idToCurrency(params.currencyId)
        }

        const result = await userAPIServiceV2.updateUser(updates)

        // V1 형식으로 반환
        let localeId = 2
        if (result.locale) {
          switch (result.locale) {
            case 'ko-KR':
              localeId = 1
              break
            case 'en-US':
              localeId = 2
              break
            case 'ja-JP':
              localeId = 3
              break
          }
        }

        // V2 currency -> V1 currencyId 변환
        const currencyId = currencyToId(result.currency)

        return {
          id: result.id,
          email: result.email,
          name: result.displayName,
          profileImage: result.profileImageUrl,
          localeId: localeId,
          currencyId: currencyId,
          createdTime: result.createdAt,
        } as any
      },
      updateUserProfileImage: async (formData: FormData) => {
        const result = await userAPIServiceV2.uploadProfileImage(formData)
        // V1 호환: filePath 필드로 반환 (store에서 사용)
        return {
          filePath: result.profileImageUrl,
          profileImage: result.profileImageUrl,
        }
      },
      getPasswordState: async () => {
        const user = await userAPIServiceV2.getUserDetail()
        return user.isPasswordResetRequired || false
      },
      changePasswordState: async (isReset: boolean) => {
        return await userAPIServiceV2.updateUser({
          isPasswordResetRequired: isReset,
        })
      },
      // 이메일로 패스워드 상태 조회
      getUserPasswordState: async (params: { email: string }) => {
        return await userAPIServiceV2.getPasswordStateByEmail(params.email)
      },
      updateUserPasswordState: async (params: {
        isPasswordResetRequired: boolean
      }) => {
        return await userAPIServiceV2.updateUser({
          isPasswordResetRequired: params.isPasswordResetRequired,
        })
      },
      /**
       * 2FA 설정 시작 (QR코드, 시크릿, 복구코드 생성)
       * V2: POST /user/me/2fa
       */
      setup2FA: async () => {
        return await userAPIServiceV2.setup2FA()
      },
      /**
       * 2FA 설정 시작 (startSetOtp는 setup2FA의 별칭)
       * 프론트엔드에서 사용하는 메서드명 호환
       * V2 응답: { qrCodeUri, secret, recoveryCodes }
       */
      startSetOtp: async () => {
        const result = await userAPIServiceV2.setup2FA()
        // V2 응답을 그대로 반환 - 프론트엔드에서 V2 필드명 직접 사용
        return result
      },
      /**
       * OTP/복구코드 검증
       * V2: POST /user/me/2fa/verify
       * V2 응답: { verified: boolean, status?: string }
       */
      verifyOtpCode: async (params: {
        code: string
        isRecoveryCode?: boolean
      }) => {
        const response = await userAPIServiceV2.verify2FA(params)
        // V2 응답을 그대로 반환 - 프론트엔드에서 verified 필드 직접 사용
        return response
      },
      /**
       * 2FA 상태 조회
       * V2: GET /user/me/2fa/status
       */
      get2FAStatus: async () => {
        return await userAPIServiceV2.get2FAStatus()
      },
      /**
       * 복구 코드 조회 (OTP 인증 필요)
       * V2: GET /user/me/2fa/recovery-codes
       */
      getRecoveryCode: async (params: {
        code: string
        isRecoveryCode?: boolean
      }) => {
        const result = await userAPIServiceV2.getRecoveryCodes(params)
        return { recoveryCode: result.recoveryCodes }
      },
      /**
       * 복구 코드 재생성
       * V2: PATCH /user/me/2fa/recovery-codes
       */
      refreshOtpRecoveryCode: async (params: {
        code: string
        isRecoveryCode?: boolean
      }) => {
        const result = await userAPIServiceV2.regenerateRecoveryCodes(params)
        return { recoveryCode: result.recoveryCodes }
      },
      /**
       * 2FA 비활성화
       * V2: DELETE /user/me/2fa
       */
      unSetOtp: async (params: { code: string; isRecoveryCode?: boolean }) => {
        return await userAPIServiceV2.disable2FA(params)
      },
      /**
       * 2FA 상태 변경 (V2에서는 백엔드가 자동 관리하므로 빈 함수)
       * 프론트에서 상태 변경 API 호출 필요 없음
       */
      change2FAState: async (_state: number) => {
        // V2에서는 백엔드가 상태를 자동으로 관리하므로 아무것도 하지 않음
        return { result: true }
      },
      getTermsAccepted: async () => {
        return true // V2에서는 기본 true
      },
      updateTermsAccepted: async () => {
        // V2에서는 약관 동의를 별도로 관리하지 않음 (회원가입 시 자동 처리)
        // web-wallet이 기대하는 응답 형식으로 반환
        return {
          success: true,
          termsAccepted: {
            termsOfService: true,
            privacyPolicy: true,
          },
        }
      },
      sendOAuthEmailVerification: async (params: {
        provider: string
        email: string
        accessToken?: string
        idToken?: string
        localeId?: number
      }) => {
        // V2: POST /oauth/verify-email
        return await userAPIServiceV2.sendOAuthEmailVerification(params)
      },
      getOauthProviderInfoList: async () => {
        // V2: GET /oauth/oauth-provider
        const providers = await userAPIServiceV2.getOAuthProviders()
        return providers
      },
    }

    // Wallet API Wrapper
    this.wallet = {
      getWalletList: async () => {
        const result = await walletAPIServiceV2.getWallets()

        // V2 응답 그대로 반환
        return {
          wallets: result.wallets || result,
          totalCount: result.totalCount,
        }
      },
      getWalletInfo: async (params: { walletId: string }) => {
        return await walletAPIServiceV2.getWallet(params.walletId)
      },
      createWallet: async (params: any) => {
        let createParams: any

        // V1 스타일 호출 (label, pin, mnemonic) vs V2 스타일 호출 (UVD, wallet, hint)
        if (params.pin && !params.UVD) {
          // V1 스타일: pin을 UVD로 변환 필요
          const {
            makeUVD,
            createDataEncryptionKey,
            encryptUVD,
            encryptMnemonic,
          } = await import('@wepin/utils')

          // 1. User의 Public Key 가져오기
          const pubKeyData = await userAPIServiceV2.getPubKey()
          if (!pubKeyData || !pubKeyData.pubKey) {
            throw new Error('User Public Key를 찾을 수 없습니다.')
          }

          // 2. UVD 생성 (nonce 0 - 새 wallet)
          const uvd = makeUVD(params.pin)
          if (!uvd) {
            throw new Error('Failed to create UVD')
          }

          // 3. DEK로 UVD 암호화
          const { DEK, encryptedDEK: b64SKey } = createDataEncryptionKey(
            pubKeyData.pubKey,
          )
          const b64Data = encryptUVD(DEK, { UVD: uvd, nonce: 0 })

          // 4. Hint 생성
          const hint = {
            data: btoa('*****' + params.pin.slice(-1)),
            length: btoa(params.pin.length.toString()),
            version: 1,
          }

          // 5. Mnemonic 암호화 (복원 시)
          let encryptedMnemonic = params.mnemonic
          if (
            params.mnemonic &&
            typeof params.mnemonic === 'string' &&
            params.mnemonic.trim().includes(' ')
          ) {
            // 평문 mnemonic인 경우 암호화
            encryptedMnemonic = encryptMnemonic(DEK, params.mnemonic.trim())
          }

          createParams = {
            UVD: { b64Data, b64SKey },
            wallet: {
              label: params.label,
              defaultWallet: true,
            },
            hint,
            mnemonic: encryptedMnemonic,
          }
        } else {
          // V2 스타일: 그대로 사용
          createParams = {
            UVD: params.UVD,
            wallet: {
              label: params.wallet?.label || params.label,
              defaultWallet: params.wallet?.defaultWallet ?? true,
            },
            hint: params.hint,
            mnemonic: params.mnemonic,
            accounts: params.accounts,
          }
        }

        const response = await walletAPIServiceV2.createWallet(createParams)

        // V2 응답이 전체 지갑 정보를 포함하는 경우 (success, data 구조)
        const walletData = (response as any).data || response

        // V1 형식으로 매핑
        return {
          wallet: {
            id: walletData.id || walletData.walletId || response.walletId,
            userId: walletData.userId || '',
            label: walletData.label || createParams.wallet.label,
            defaultWallet:
              walletData.defaultWallet ?? createParams.wallet.defaultWallet,
            state: walletData.status || walletData.state || 'created', // V2: status -> V1: state
            pinState: walletData.pinState || 'locked',
            createdTime: walletData.createdAt || new Date().toISOString(),
            exportedTime: walletData.exportedTime || '',
            order: walletData.order || 0,
          },
          account: null, // V2에서는 별도로 account 생성 필요
        }
      },
      backupWallet: async (params: any) => {
        const response = await walletAPIServiceV2.backupWallet(
          params.walletId,
          params.UVD,
        )
        return { mnemonic: response.mnemonic }
      },
      updateWallet: async (pathParams: { walletId: string }, params: any) => {
        const response = await walletAPIServiceV2.updateWallet(
          pathParams.walletId,
          params,
        )
        return { wallet: response }
      },
      deleteWallet: async (pathParams: { walletId: string }, params: any) => {
        // V2: deleteUVD를 UVD로 전달 (verifyUVD는 검증용, deleteUVD가 실제 삭제용)
        const response = await walletAPIServiceV2.deleteWallet(
          pathParams.walletId,
          params.deleteUVD,
        )
        return { result: true, mnemonic: response.mnemonic }
      },
      verifyPin: async (params: any) => {
        return await walletAPIServiceV2.verifyPin(params.walletId, params.UVD)
      },
      changePin: async (params: any) => {
        return await walletAPIServiceV2.changePin(
          params.walletId,
          params.UVD,
          params.newUVD,
          params.hint,
        )
      },
      getResetPin: async (params: { walletId: string }) => {
        return await walletAPIServiceV2.resetPinRetries(params.walletId)
      },
      resetPinTryCount: async (params: { id: string }) => {
        // V2: resetPinRetries와 동일
        return await walletAPIServiceV2.resetPinRetries(params.id)
      },
      getWalletCoreKeyInfo: async (params: { id: string }) => {
        // V2: getWallet으로 wallet 정보 조회 (nonce, pubKey 포함)
        const walletInfo = await walletAPIServiceV2.getWallet(params.id)
        return {
          nonce: walletInfo.nonce,
          b64PubKey: walletInfo.b64PubKey,
        }
      },
      getWalletKeyInfo: async (params: { id: string }) => {
        // V2: getKeyInfo로 PIN 잠금 상태 정보 조회
        const keyInfo = await walletAPIServiceV2.getKeyInfo(params.id)
        return {
          maxTryCnt: keyInfo.maxTryCnt,
          remainTryCnt: keyInfo.remainTryCnt,
          isBlocked: keyInfo.isBlocked,
          isRegistered: keyInfo.isRegistered,
          registeredTime: keyInfo.registeredTime,
          necessaryTime: keyInfo.necessaryTime,
        }
      },
      updateWalletState: async (_params: any) => {
        // V2에서는 별도 상태 업데이트 API 없음
        return { success: true }
      },
    }

    // Account API Wrapper
    this.account = {
      getAccountList: async () => {
        // V2에서는 wallet API 응답에 이미 accounts가 포함되어 있음
        // WalletAPIStore에 캐싱된 데이터를 사용하도록 빈 배열 반환
        // (실제 데이터는 wallet.ts:126-143에서 accountListByWalletKey에 캐싱됨)
        return { accounts: [] }
      },
      getAccount: async (params: { accountId: string; walletId?: string }) => {
        // V2: walletId 필요 - params에서 가져오거나 localStorage에서 가져옴
        const walletId =
          params.walletId || localStorage.getItem('defaultWalletId')
        if (!walletId) {
          throw new Error('walletId is required for getAccount in V2')
        }
        const accountData = await accountAPIServiceV2.getAccountById(
          walletId,
          params.accountId,
        )

        return {
          account: {
            ...accountData,
            currency: accountData.currency,
            currencyId: accountData.currencyId,
          },
        }
      },
      createAccount: async (params: any) => {
        const walletId =
          params.walletId || localStorage.getItem('defaultWalletId')

        // V1 → V2 파라미터 변환
        // V1: { walletId, account: { coinId, label }, UVD }
        // V2: { accounts: [{ currencyId, accountIndex, label }], UVD }
        // V2에서 currencyId는 문자열 ("BITCOIN", "ETHEREUM" 등)
        const v2Params = {
          accounts: [
            {
              currencyId:
                params.account?.currencyId ||
                params.account?.coinId?.toString(),
              accountIndex: params.account?.accountIndex || 0,
              label: params.account?.label || '',
            },
          ],
          UVD: params.UVD,
        }

        const response = await accountAPIServiceV2.createAccount(
          walletId,
          v2Params,
        )

        // V2 응답을 V1 형식으로 변환
        // V2: [{ id, walletId, currencyId, addresses, label, currency, ... }]
        // V1: { account: { id, walletId, coinId, address, label, ... } }
        if (Array.isArray(response) && response.length > 0) {
          const account = response[0]
          return {
            account: {
              id: account.id,
              walletId: account.walletId,
              coinId: account.currencyId, // V2의 currencyId를 coinId로 매핑
              currencyId: account.currencyId, // V2 currencyId 보존
              currency: account.currency, // V2 currency 객체 보존
              address: account.addresses?.[0] || account.address,
              label: account.label,
              balance: account.balance || '0',
              createdTime: account.createdAt,
              // V1 호환을 위한 추가 필드들
              state: 1,
              onchainState: 1,
              defaultAccount: false,
              addressPath: account.accountData?.addressPath || '',
              order: 0,
            },
          }
        }

        // 배열이 아니면 그대로 반환 (fallback)
        return { accounts: response }
      },
      modifyAccount: async (pathParams: { accountId: string }, params: any) => {
        const walletId =
          params.walletId || localStorage.getItem('defaultWalletId')
        return await accountAPIServiceV2.updateAccount(
          walletId,
          pathParams.accountId,
          params,
        )
      },
      deleteAccount: async (params: {
        accountId: string
        walletId?: string
      }) => {
        const walletId =
          params.walletId || localStorage.getItem('defaultWalletId')
        if (!walletId) {
          throw new Error('walletId is required for deleteAccount in V2')
        }
        await accountAPIServiceV2.deleteAccount(walletId, params.accountId)
        return { result: true, success: true }
      },
    }

    // Coin API Wrapper
    this.coin = {
      getCoinInfoList: async (params?: {
        ids?: string | string[]
        page?: number
        locale?: number
      }) => {
        // V2에서는 페이지네이션 없음, store에서 캐시된 데이터 사용
        const currencyStore = useCurrencyAPIStore()

        // native 코인이 없으면 로드
        const existingNativeCoins = currencyStore.getNativeCoins
        if (existingNativeCoins.length === 0) {
          await currencyStore.fetchCurrencyList({ type: 'native' })
        }

        // native 코인만 필터링
        const coins = currencyStore.getNativeCoins

        // V2는 페이지네이션이 없으므로 첫 번째 호출(page === 1)에만 데이터 반환
        // 이후 호출에는 빈 배열 반환하여 무한 루프 방지
        if (params?.page && params.page > 1) {
          return { coins: [] }
        }

        // V1 형식으로 변환 (coin.ts에서 기대하는 형식)
        // V2는 string ID를 사용하므로 numeric ID로 변환 필요
        const stringToNumericId = (str: string): number => {
          let hash = 0
          for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i)
            hash = (hash << 5) - hash + char
            hash = hash & hash // Convert to 32bit integer
          }
          return Math.abs(hash)
        }

        return {
          coins: coins.map((coin: any) => ({
            id: stringToNumericId(coin.id), // V2 string ID를 numeric으로 변환
            name: coin.name,
            symbol: coin.ticker || coin.symbol,
            ticker: coin.ticker,
            decimals: coin.decimals,
            iconUrl: coin.iconUrl,
            cgkId: coin.cgkId,
            cmcId: coin.cmcId,
            family: coin.family,
            color: coin.color,
            isTestnet: coin.isTestnet ? 1 : 0,
            network: coin.id, // V2에서는 id가 network 역할
            // V2 blockExplorerUrl -> V1 explorer 필드 변환
            explorerUrl: coin.blockExplorerUrl?.url,
            explorerAddress: coin.blockExplorerUrl?.address?.replace(
              '$address',
              '{address}',
            ),
            explorerTransaction: coin.blockExplorerUrl?.tx?.replace(
              '$hash',
              '{txID}',
            ),
            explorerContract: coin.blockExplorerUrl?.contract?.replace(
              '$address',
              '{address}',
            ),
            property: {
              ...coin.property,
              _v2CurrencyId: coin.id, // V2 원본 ID를 property에 보존
            },
          })),
        }
      },
      getCoinInfoListWithoutSession: async (_params?: any) => {
        const coins = await currencyAPIService.getCurrencyList({
          type: 'native',
        })
        return {
          coins: coins.map((coin: any) => ({
            id: coin.id,
            name: coin.name,
            symbol: coin.ticker || coin.symbol,
            ticker: coin.ticker,
            decimals: coin.decimals,
            iconUrl: coin.iconUrl,
            cgkId: coin.cgkId,
            cmcId: coin.cmcId,
            family: coin.family,
            color: coin.color,
            isTestnet: coin.isTestnet ? 1 : 0,
            network: coin.id,
            property: coin.property,
          })),
        }
      },
      getTokenList: async () => {
        const tokens = await currencyAPIService.getCurrencyList({
          type: 'token',
        })
        return tokens
      },
      getSearchTokenList: async (params: any) => {
        // V2에서는 검색 API 없음, 전체 목록에서 필터링
        const tokens = await currencyAPIService.getCurrencyList({
          type: 'token',
        })
        if (params.search) {
          return tokens.filter(
            (token: any) =>
              token.name?.toLowerCase().includes(params.search.toLowerCase()) ||
              token.ticker?.toLowerCase().includes(params.search.toLowerCase()),
          )
        }
        return tokens
      },
      getTokenInfoList: async (params: {
        name?: string
        symbol?: string
        contract?: string
        page?: number
        locale?: number
      }) => {
        // V2에서는 검색 API 없음, store에서 캐시된 데이터 사용
        const currencyStore = useCurrencyAPIStore()

        // 토큰이 없으면 로드
        const existingTokens = currencyStore.getTokens
        if (existingTokens.length === 0) {
          await currencyStore.fetchCurrencyList({ type: 'token' })
        }

        // 토큰만 필터링
        let filtered = currencyStore.getTokens

        // 검색어가 있으면 필터링
        if (params.name || params.symbol || params.contract) {
          const searchValue = (
            params.name ||
            params.symbol ||
            params.contract ||
            ''
          ).toLowerCase()
          filtered = filtered.filter(
            (token: any) =>
              token.name?.toLowerCase().includes(searchValue) ||
              token.ticker?.toLowerCase().includes(searchValue) ||
              token.symbol?.toLowerCase().includes(searchValue) ||
              token.contractAddress?.toLowerCase().includes(searchValue),
          )
        }

        // 페이지네이션 적용
        const PAGE_SIZE = 100
        const page = params.page || 1
        const startIndex = (page - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        const paginatedTokens = filtered.slice(startIndex, endIndex)

        // V1 형식으로 변환 (coin.ts에서 기대하는 형식)
        return {
          tokens: paginatedTokens.map((token: any, index: number) => ({
            id: index, // V2에는 numeric id가 없으므로 인덱스 사용
            name: token.name,
            symbol: token.ticker || token.symbol,
            ticker: token.ticker,
            decimals: token.decimals,
            iconUrl: token.iconUrl,
            contract: token.contractAddress || '',
            coinId: token.parentCurrencyId, // V2의 parentCurrencyId (string)
            contractAddress: token.contractAddress,
            parentCurrencyId: token.parentCurrencyId,
            coinGeckoId: token.cgkId,
            // V2 Currency ID를 property에 저장
            property: {
              _v2CurrencyId: token.id, // V2 Currency ID (예: "KLAYTN-ERC20/0x...")
            },
            // parentCurrency 정보를 coin 형식으로 포함 (Token 클래스에서 사용)
            _parentCurrency: token.parentCurrency
              ? {
                  id: token.parentCurrency.id,
                  name: token.parentCurrency.name,
                  symbol: token.parentCurrency.ticker,
                  ticker: token.parentCurrency.ticker,
                  iconUrl: token.parentCurrency.iconUrl,
                  cgkId: token.parentCurrency.cgkId,
                  // CRITICAL: network는 parentCurrency.id로 설정 (account.currencyId와 매칭)
                  network: token.parentCurrency.id, // "KLAYTN", "ETHEREUM" 등
                  property: {
                    _v2CurrencyId: token.parentCurrency.id,
                  },
                }
              : undefined,
          })),
        }
      },
      getCoinPrice: async () => {
        // V2에는 가격 API 없음, CoinGecko 캐시 사용
        return []
      },
      getCoinPriceByAccount: async () => {
        // V2에는 가격 API 없음
        return []
      },
    }

    // Token API (토큰은 coin API의 일부)
    this.token = this.coin

    // Transaction API Wrapper
    this.transaction = {
      // V1 호환 메서드: TransactionExecutor에서 사용
      getTransactionPrepareData: async <_T extends string>(params: {
        accountId: string
        to: string
        amount?: string
        tokenId?: string
        contract?: string
        isNft?: string
      }) => {
        // V2 prepareTransaction 호출
        const prepareResponse =
          await transactionAPIServiceV2.prepareTransaction({
            accountId: params.accountId,
            type: 'transfer',
            recipient: params.to,
            amount: params.amount || '0',
          })

        // V2 응답을 V1 형식으로 변환
        const detail = prepareResponse.transaction || prepareResponse
        return {
          detail,
          ...prepareResponse,
        }
      },
      prepare: async (params: any) => {
        return await transactionAPIServiceV2.prepareTransaction(params)
      },
      sign: async (params: any) => {
        return await transactionAPIServiceV2.signTransaction(params)
      },
      broadcast: async (params: any) => {
        return await transactionAPIServiceV2.broadcastTransaction(params)
      },
      getTxListByMe: async (params?: any) => {
        return await transactionAPIServiceV2.getTransactions(params)
      },
      getTxListByAccount: async (params: any) => {
        return await transactionAPIServiceV2.getTransactions({
          accountIds: [params.accountId],
          ...params,
        })
      },
      getTxDetail: async (params: { txId: string }) => {
        const txs = await transactionAPIServiceV2.getTransactions({
          txIds: [params.txId],
        })
        return txs.transactions?.[0] || null
      },
      checkAddress: async (params: any) => {
        return await transactionAPIServiceV2.validateAddress({
          accountId: params.accountId,
          toAddress: params.toAddress,
          isContract: params.isContract,
        })
      },
      checkAddressValidation: async (_params: {
        coinId: string
        address: string
      }) => {
        // V1에서는 coinId를 사용했지만, V2에서는 accountId를 사용
        // 하지만 InputDestination에서는 account.value?.id를 사용할 수 없으므로
        // 이 메서드는 V1 호환성을 위해 남겨두되, 실제로는 사용하지 않음
        throw new Error(
          'checkAddressValidation is deprecated. Use V2 validateAddress with accountId instead.',
        )
      },
      verifySignature: async (_params: any) => {
        throw new Error('Signature verification not implemented in V2')
      },
      getAccountTransactionList: async (params: {
        accountId: string
        accountTokenId?: string
        contract?: string
        page?: number
      }) => {
        // V2: getTransactions 사용
        // 토큰 트랜잭션의 경우 accountTokenId를 accountIds로 사용
        const accountIdToQuery = params.accountTokenId || params.accountId

        const response = await transactionAPIServiceV2.getTransactions({
          accountIds: [accountIdToQuery],
        })
        const transactions = response.transactions || []

        // V2 응답을 V1 형식으로 변환
        return transactions.map((tx: any) => {
          // V2 응답에서 decimals 추출
          const decimals =
            tx.account?.accountData?.accountInfo?.decimal ||
            tx.account?.currency?.decimals ||
            18

          return {
            id: tx.id,
            accountId: tx.accountId,
            accountTokenId: params.accountTokenId, // 토큰 트랜잭션인 경우 accountTokenId 추가
            txid: tx.txHash,
            value: tx.amount,
            status:
              tx.status?.toLowerCase() === 'confirmed'
                ? 'confirm'
                : tx.status?.toLowerCase(),
            createDate: tx.transactionDate || tx.createdAt,
            to: Array.isArray(tx.toAddress) ? tx.toAddress[0] : tx.toAddress,
            type:
              tx.transactionType?.toLowerCase() === 'out' ? 'send' : 'receive',
            // V2 추가 필드
            decimals,
            // V2 원본 필드 보존
            _v2: tx,
          }
        })
      },
      getTransactionReceipt: async (params: {
        txIds?: string[]
        accountIds?: string[]
        txHashes?: string[]
      }) => {
        // V2: /transaction/receipt API 호출 (배열을 txIds=a&txIds=b 형식으로 전송)
        const response = await transactionAPIServiceV2.getTransactionReceipt({
          txIds: params.txIds,
          accountIds: params.accountIds,
          txHashes: params.txHashes,
        })
        return response
      },
    }

    // Account Token API Wrapper
    this.accountToken = {
      addToken: async (params: any) => {
        // V2: addToken API 호출 - walletId 필수
        if (!params.walletId) {
          throw new Error('walletId is required for addToken')
        }
        return await accountTokenAPIServiceV2.addToken(params)
      },
      addAccountToken: async (params: any) => {
        // Alias for addToken (backward compatibility) - walletId 필수
        if (!params.walletId) {
          throw new Error('walletId is required for addAccountToken')
        }
        return await accountTokenAPIServiceV2.addToken(params)
      },
      getAccountToken: async (params?: any) => {
        return await accountTokenAPIServiceV2.getTokenList(params || {})
      },
      getTokenList: async (_params?: any) => {
        // V2에서는 wallet API 응답에 이미 accountTokens가 포함되어 있음
        // 캐시된 데이터를 사용하도록 빈 배열 반환
        return []
      },
      getAccountTokenDetail: async (params: { id: string }) => {
        // V2에서는 단일 토큰 조회 API 없음, 목록에서 필터링
        const tokens = await accountTokenAPIServiceV2.getTokenList({
          parentAccountId: params.id,
        })
        return tokens.find((token: any) => token.id === params.id)
      },
      modifyAccountToken: async (pathParams: { id: string }, params: any) => {
        return await accountTokenAPIServiceV2.updateToken(
          pathParams.id,
          params.label,
        )
      },
      deleteAccountToken: async (params: { id: string }) => {
        await accountTokenAPIServiceV2.deleteToken(params.id)
        return { success: true }
      },
      deleteToken: async (params: { id: string; walletId?: string }) => {
        // Alias for deleteAccountToken
        await accountTokenAPIServiceV2.deleteToken(params.id, params.walletId)
        return { result: true }
      },
    }

    // NFT API Wrapper
    this.nft = {
      refreshNFTList: async (params: {
        accountIds: string[]
        walletId?: string
      }) => {
        // V2: walletId가 있으면 해당 지갑만, 없으면 모든 지갑 새로고침
        // walletId 파라미터를 우선 사용, 없으면 undefined로 전체 새로고침
        const _result = await nftAPIService.refreshNFTs({
          walletId: params.walletId, // undefined면 전체 새로고침
        })
        return { result: true }
      },
      getNFTList: async (params?: { walletId: string; page?: string }) => {
        const result = await nftAPIService.getNFTList({
          walletId: params?.walletId,
          page: params?.page ? parseInt(params.page) : undefined,
        })

        // V2 응답(items)을 V1 형식(nftTokens)으로 변환
        return {
          nftTokens: result.items,
          total: result.total,
          page: result.page,
          limit: result.limit,
        }
      },
      getNFTCount: async () => {
        const result = await nftAPIService.getNFTList({})
        return { count: result.total || result.items.length }
      },
      getNFTInfo: async (params: { nftTokenId: string }) => {
        // V2에서는 상세 조회 API 사용
        const nft = await nftAPIService.getNFTById(params.nftTokenId)
        return { nftToken: nft }
      },
      getSupportNetworks: async () => {
        const networks = await nftAPIService.getSupportedNetworks()
        return networks
      },
      getSupportedNFTList: async () => {
        // V2에서는 지원되는 네트워크 목록으로 대체
        const networks = await nftAPIService.getSupportedNetworks()
        return {
          supportNetworkList: networks.map((network: string) => ({
            network: network,
            name: network,
          })),
        }
      },
      // V1 호환: POST /nfttoken/add - minting 후 NFT 정보 등록
      addNFTInfo: async (params: { email: string; token: any }) => {
        console.log('[WepinBackendAPI] addNFTInfo called:', params)
        try {
          const response = await apiInterceptor.request('/nfttoken/add', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
          })

          if (!response.ok) {
            console.error(
              '[WepinBackendAPI] addNFTInfo failed:',
              response.statusText,
            )
            // 에러가 발생해도 minting 프로세스가 중단되지 않도록 성공 반환
            return { result: true }
          }

          const data = await response.json()
          console.log('[WepinBackendAPI] addNFTInfo response:', data)
          return data
        } catch (error) {
          console.error('[WepinBackendAPI] addNFTInfo error:', error)
          // 에러가 발생해도 minting 프로세스가 중단되지 않도록 성공 반환
          return { result: true }
        }
      },
    }

    // NFT Contract API (V2에서는 별도 API 없음)
    // V2 NFT 응답에 contract 정보가 포함되어 있으므로 빈 응답 반환
    this.nftcontract = {
      getNFTContractList: async (_params: { ids?: number[] }) => {
        // V2에서는 NFT 응답에 이미 contract 정보가 포함되어 있음
        // 빈 배열 반환 (nftcontract.ts에서 에러 방지)
        return { nftContracts: [] }
      },
      getNFTContract: async (_params: { nftContractId: string }) => {
        // V2에서는 NFT 응답에 이미 contract 정보가 포함되어 있음
        return { nftContract: null }
      },
      getNFTContractWithoutSession: async () => {
        return { nftContract: null }
      },
    }

    // Info API (V2: GET /api/v2/apps/locales, GET /api/v2/apps/currencies)
    this.info = {
      getCurrencyList: async () => {
        // V2: GET /api/v2/apps/currencies (사용자 설정용 통화 목록: USD, KRW 등)
        const response = await apiInterceptor.request('/apps/currencies')
        if (!response.ok) {
          throw new Error(`Failed to fetch currencies: ${response.statusText}`)
        }
        const result = await response.json()

        // V2 응답: { success: true, data: { currencies: [...] } }
        // V1 형식으로 변환: { currency: [...] }
        const currencies = result.data?.currencies || result.currencies || []

        // V1 형식으로 매핑 (id는 숫자, name은 code)
        const mappedCurrencies = currencies.map((curr: any) => ({
          id: curr.id,
          name: curr.code, // USD, KRW 등
          symbol: curr.symbol,
          code: curr.code,
        }))

        return { currency: mappedCurrencies }
      },
      getCurrencyDetail: async (params: { currencyId: string }) => {
        return await currencyAPIService.getCurrencyById(params.currencyId)
      },
      getLocaleList: async () => {
        // V2: GET /api/v2/apps/locales
        const response = await apiInterceptor.request('/apps/locales')
        if (!response.ok) {
          throw new Error(`Failed to fetch locales: ${response.statusText}`)
        }
        const result = await response.json()

        // V2 응답: { success: true, data: { locales: [...] } }
        // V1 형식으로 변환: { locale: [...] }
        const locales = result.data?.locales || result.locales || []

        // V1 형식으로 매핑 (localeGroup 필드 추가)
        const mappedLocales = locales.map((loc: any) => ({
          id: loc.id,
          code: loc.code,
          name: loc.nativeName || loc.name, // 네이티브 문자로 표시: "한국어", "English", "日本語"
          localeGroup: loc.region, // "KR", "US", "JP" 등 - locale.ts에서 사용
        }))

        return { locale: mappedLocales }
      },
      getLocaleDetail: async (params: { localeId: number }) => {
        // V2: GET /api/v2/apps/locales/:id
        const response = await apiInterceptor.request(
          `/apps/locales/${params.localeId}`,
        )
        if (!response.ok) {
          throw new Error(`Failed to fetch locale: ${response.statusText}`)
        }
        const result = await response.json()
        const locale = result.data?.locale || result.locale
        return {
          id: locale.id,
          code: locale.code,
          name: locale.nativeName || locale.name, // 네이티브 문자로 표시
          localeGroup: locale.region,
        }
      },
    }

    // Project API (V2에서는 미지원 - 빈 응답 반환)
    this.project = {
      // App 관련
      getAppPromotion: async () => null,
      getReferralAppPromotionList: async () => [],
      getAppPromotionMintContent: async () => null,
      getUserAppDetail: async () => {
        // V2: GET /apps/me 호출
        try {
          const apps = await userAPIServiceV2.getAppsMe()

          const userApps = apps.map((app) => ({
            appId: app.id,
            appName: app.name,
            iconUrl: app.iconImageUrl || '',
            // supportedAssets가 ["NO_RESTRICTION"]이면 모든 asset을 허용
            supportedAssets: app.supportedAssets || [],
          }))

          return userApps
        } catch (error) {
          // Error handled silently
          return []
        }
      },
      getUserApp: async () => ({
        apps: [],
        totalCount: 0,
      }),
      getUserAppDetailByID: async () => null,
      getAppList: async () => ({
        apps: [],
        totalCount: 0,
      }),
      getAppDetail: async () => null,
      getAppByPopular: async () => ({
        apps: [],
        totalCount: 0,
      }),
      getAppByNew: async () => ({
        apps: [],
        totalCount: 0,
      }),
      registerApp: async () => ({ success: true }),
      deregisterApp: async () => ({ success: true }),
      likeApp: async () => ({ success: true }),
      unlikeApp: async () => ({ success: true }),

      // Project 관련
      createProject: async () => null,
      modifyProject: async () => null,
      deleteProject: async () => ({ result: false }),
      registerProject: async () => ({ id: '' }),
      deregisterProject: async () => ({ success: true }),
      getProject: async () => null,
      getProjectList: async () => ({ projects: [], totalCount: 0 }),
      getPopularProjectList: async () => ({ projects: [], totalCount: 0 }),
      getNewProjectList: async () => ({ projects: [], totalCount: 0 }),
      getProjectTokenList: async () => ({ tokens: [] }),
      getProjectNFTList: async () => ({ nfts: [] }),
      setLikeProject: async () => ({ success: true }),
      unsetLikeProject: async () => ({ success: true }),
      updateProjectJoinUserInfo: async () => null,
      dropOutUser: async () => ({ success: true }),
      getUserProjectList: async () => ({ projectJoinInfo: [] }),
    }

    // Droplink API
    this.dropLink = {
      getCampaign: droplinkApiService.getCampaign,
      getCampaignLanding: droplinkApiService.getCampaignLanding,
      getCampaignDetail: droplinkApiService.getCampaignDetail,
      getClaim: droplinkApiService.getClaim,
      requestCampaign: droplinkApiService.requestCampaign,
      addCampaignQuestHistory: droplinkApiService.addCampaignQuestHistory,
      requestClaim: droplinkApiService.requestClaim,
      referralCampaign: droplinkApiService.referralCampaign,
      requestEmailCampaign: droplinkApiService.requestEmailCampaign,
      requestCompleteEmailCampaign:
        droplinkApiService.requestCompleteEmailCampaign,
      getReferralCodeCampaign: droplinkApiService.getReferralCodeCampaign,
      checkEmailJoinCampaign: droplinkApiService.checkEmailJoinCampaign,
    }

    // Notice API (V2에서는 미지원)
    this.notice = {
      getNoticeList: async () => [],
      getNotice: async () => null,
      deleteNotice: async () => ({ success: true }),
    }

    // Account Balance API
    this.accountBalance = {
      getAccountBalance: async (params: { accountId: string }) => {
        return await walletAPIServiceV2.getAccountBalances([params.accountId])
      },
    }
  }

  static getInstance(): WepinBackendAPIV2 {
    if (!WepinBackendAPIV2.instance) {
      WepinBackendAPIV2.instance = new WepinBackendAPIV2()
    }
    return WepinBackendAPIV2.instance
  }
}

export default WepinBackendAPIV2
